import { useState, useEffect } from 'react';
import { usersGraphqlService, ResourceAvailability } from '@/services/api/users/usersGraphqlService';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/services/core/apiClient';
import { useErrorHandler } from '@/hooks/useErrorHandler';

interface UserData {
  id: number;
  user_name: string;
  email: string;
  user_type: string;
  phone_number?: string;
}

interface ScheduleConfig {
  resourceAvailabilityId: number | null;
  fromDate: string;
  toDate: string;
  timezone: string;
  daySlots: Record<string, {
    enabled: boolean;
    slots: Array<{
      id: string;
      fromTime: string;
      toTime: string;
      availabilityDayTimeId?: number | null;
    }>;
  }>;
}

interface OutlookConfig {
  useOutlookEmail: boolean;
  outlookEmail: string;
}

interface UseEditUserResult {
  user: UserData | null;
  scheduleConfig: ScheduleConfig | null;
  outlookConfig: OutlookConfig | null;
  loading: boolean;
  submitting: boolean;
  updateUser: (data: any) => Promise<boolean>;
}

// Convert 24-hour time (HH:MM:SS) to 12-hour format (H:MM AM/PM)
const convertTo12Hour = (time24: string): string => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  let hoursNum = parseInt(hours, 10);
  const period = hoursNum >= 12 ? 'PM' : 'AM';
  hoursNum = hoursNum % 12 || 12;
  return `${hoursNum}:${minutes} ${period}`;
};

// Map numeric day of week to day name
const dayOfWeekToName: Record<number, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday'
};

export const useEditUser = (userId: string): UseEditUserResult => {
  const { token, hasuraToken, user: authUser } = useAuth();
  const { handleError, handleSuccess } = useErrorHandler();
  const [user, setUser] = useState<UserData | null>(null);
  const [scheduleConfig, setScheduleConfig] = useState<ScheduleConfig | null>(null);
  const [outlookConfig, setOutlookConfig] = useState<OutlookConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchUserAndSchedule = async () => {
      if (!hasuraToken || !userId) {
        setLoading(false);
        return;
      }

      try {
        // Fetch user data, schedule data, and login info in parallel (edit mode only)
        const [userResponse, scheduleResponse, loginInfoResponse] = await Promise.all([
          usersGraphqlService.getUserById(hasuraToken, parseInt(userId)),
          usersGraphqlService.getResourceAvailability(hasuraToken, parseInt(userId)),
          usersGraphqlService.getHBoxUserLoginInfo(hasuraToken, parseInt(userId))
        ]);

        if (userResponse.success && userResponse.data?.pep_vw_user_dashboard?.[0]) {
          setUser(userResponse.data.pep_vw_user_dashboard[0]);
        } else {
          handleError('User not found');
        }

        if (loginInfoResponse.success) {
          const loginInfo = loginInfoResponse.data?.api_hboxuser_by_pk;
          setOutlookConfig({
            useOutlookEmail: loginInfo?.login_type === 'OUTLOOK',
            outlookEmail: loginInfo?.outlook_mapping?.principal_email || ''
          });
        }

        // Process schedule data if available
        if (scheduleResponse.success && scheduleResponse.data?.ref_resource_availability?.[0]) {
          const availability = scheduleResponse.data.ref_resource_availability[0];
          
          // Initialize default day slots
          const daySlots: ScheduleConfig['daySlots'] = {
            Monday: { enabled: false, slots: [{ id: 'Monday-1', fromTime: '', toTime: '' }] },
            Tuesday: { enabled: false, slots: [{ id: 'Tuesday-1', fromTime: '', toTime: '' }] },
            Wednesday: { enabled: false, slots: [{ id: 'Wednesday-1', fromTime: '', toTime: '' }] },
            Thursday: { enabled: false, slots: [{ id: 'Thursday-1', fromTime: '', toTime: '' }] },
            Friday: { enabled: false, slots: [{ id: 'Friday-1', fromTime: '', toTime: '' }] },
            Saturday: { enabled: false, slots: [{ id: 'Saturday-1', fromTime: '', toTime: '' }] },
            Sunday: { enabled: false, slots: [{ id: 'Sunday-1', fromTime: '', toTime: '' }] }
          };

          // Populate day slots from API response
          if (availability.resourse_availbility && availability.resourse_availbility.length > 0) {
            availability.resourse_availbility.forEach((slot, index) => {
              const dayName = dayOfWeekToName[slot.day_of_week];
              if (dayName) {
                if (!daySlots[dayName].enabled) {
                  // First slot for this day - enable and replace default
                  daySlots[dayName] = {
                    enabled: true,
                    slots: [{
                      id: `${dayName}-${index}`,
                      fromTime: convertTo12Hour(slot.available_start_time),
                      toTime: convertTo12Hour(slot.available_end_time),
                      availabilityDayTimeId: slot.resource_availability_id
                    }]
                  };
                } else {
                  // Additional slot for this day
                  daySlots[dayName].slots.push({
                    id: `${dayName}-${index}`,
                    fromTime: convertTo12Hour(slot.available_start_time),
                    toTime: convertTo12Hour(slot.available_end_time),
                    availabilityDayTimeId: slot.resource_availability_id
                  });
                }
              }
            });
          }

          setScheduleConfig({
            resourceAvailabilityId: availability.resource_availability_id,
            fromDate: availability.available_from_date,
            toDate: availability.available_to_date,
            timezone: availability.user_timezone || 'US/Pacific',
            daySlots
          });
        }
      } catch (err) {
        handleError(err, 'Failed to fetch user details');
      } finally {
        setLoading(false);
      }
    };

    fetchUserAndSchedule();
  }, [hasuraToken, userId, handleError]);

  // Convert 12-hour time to 24-hour format (HH:MM:SS)
  const convertTo24Hour = (time12: string): string => {
    if (!time12) return '';
    const [time, period] = time12.split(' ');
    const [hours, minutes] = time.split(':');
    let hoursNum = parseInt(hours, 10);
    
    if (period === 'PM' && hoursNum !== 12) {
      hoursNum += 12;
    } else if (period === 'AM' && hoursNum === 12) {
      hoursNum = 0;
    }
    
    return `${hoursNum.toString().padStart(2, '0')}:${minutes}:00`;
  };

  // Map day name to numeric value
  // NOTE: Normalize keys to avoid casing mismatches (e.g. "MONDAY" vs "Monday").
  const dayNameToNumber: Record<string, number> = {
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
    sunday: 7,
  };

  const getDayOfWeekNumber = (dayName: string): number | undefined => {
    const key = (dayName || '').trim().toLowerCase();
    return dayNameToNumber[key];
  };

  const updateUser = async (formData: any): Promise<boolean> => {
    if (!authUser?.id || !userId) {
      handleError('Authentication required');
      return false;
    }

    setSubmitting(true);

    try {
      // Map user type values: CS->CS, PES->PE, VPE->ES (same as AddUser)
      const userTypeMapping: Record<string, string> = {
        CS: "CS",
        PES: "PE",
        VPE: "ES",
      };

      const requestBody: any = {
        first_name: formData.firstname,
        last_name: formData.lastname,
        email: formData.email,
        user_type: userTypeMapping[formData.usertype] || formData.usertype,
        phone_number: formData.phonenumber,
        login_type: formData.useOutlookEmail ? 'OUTLOOK' : 'PASSWORD',
        principal_email: formData.useOutlookEmail ? formData.outlookEmail?.trim() || null : null
      };

      // Only include password if it's provided (not empty)
      if (formData.password && formData.password.trim().length > 0) {
        requestBody.password = formData.password;
      }

      // Build dto with schedule configuration
      if (formData.scheduleConfig) {
        const { fromDate, toDate, timezone, daySlots } = formData.scheduleConfig;
        
        // Build dayTimes array from enabled days
        const dayTimes: Array<{
          availabilityDayTimeId: number | null;
          dayOfWeek: number;
          availableStartTime: string;
          availableEndTime: string;
        }> = [];

        Object.entries(daySlots).forEach(([dayName, dayData]: [string, any]) => {
          if (dayData.enabled && dayData.slots) {
            dayData.slots.forEach((slot: any) => {
              if (slot.fromTime && slot.toTime) {
                const dayOfWeek = getDayOfWeekNumber(dayName);
                if (!dayOfWeek) return;
                dayTimes.push({
                  availabilityDayTimeId: slot.availabilityDayTimeId || null,
                  dayOfWeek,
                  availableStartTime: convertTo24Hour(slot.fromTime),
                  availableEndTime: convertTo24Hour(slot.toTime)
                });
              }
            });
          }
        });

        requestBody.dto = {
          resourceAvailabilityId: scheduleConfig?.resourceAvailabilityId || null,
          userId: parseInt(userId),
          availableFromDate: fromDate,
          availableToDate: toDate,
          userTimezone: timezone,
          dayTimes
        };
      }

      const endpoint = `/api/ngpep/${authUser.id}/ed/${userId}`;
      const response = await apiClient.put(endpoint, requestBody, token);

      if (response.success) {
        handleSuccess('User updated successfully');
        return true;
      } else {
        handleError(response.error || 'Failed to update user');
        return false;
      }
    } catch (err) {
      handleError(err, 'Failed to update user');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  return {
    user,
    scheduleConfig,
    outlookConfig,
    loading,
    submitting,
    updateUser,
  };
};
